import {
  INCIDENT_ACTION_STATUSES,
  INCIDENT_EVENT_KINDS,
  INCIDENT_SOURCES,
  INCIDENT_STATUSES,
  INCIDENT_TEXT_LIMITS,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { operationsListItemSchema } from './operations.ts';

// Incident reporting (docs/plans/phase3-operations-modules.md §1.3). Free text travels in
// bodies only, never in URLs or query strings.

const L = INCIDENT_TEXT_LIMITS;
const optionalText = (max: number) => z.string().trim().max(max).nullable();

export const incidentOptionsSchema = z.object({
  categories: z.array(operationsListItemSchema),
  /** In order of harm, the least first. */
  severities: z.array(operationsListItemSchema),
});
export type IncidentOptionsView = z.infer<typeof incidentOptionsSchema>;

const reportFields = {
  categoryCode: z.string().trim().min(1).max(40),
  severityCode: z.string().trim().min(1).max(40),
  /** When it happened. */
  occurredAt: z.iso.datetime({ offset: true }),
  location: z.string().trim().min(1).max(L.location),
  description: z.string().trim().min(1).max(L.description),
  immediateAction: optionalText(L.immediateAction),
  patientId: z.uuid().nullable(),
  visitId: z.uuid().nullable(),
};

export const reportIncidentRequestSchema = z.object({
  clientRequestId: z.uuid(),
  ...reportFields,
  equipmentText: optionalText(L.equipmentText),
  /** Hide the reporter's name from colleagues; the safety lead still sees it. */
  anonymous: z.boolean(),
});
export type ReportIncidentRequest = z.infer<typeof reportIncidentRequestSchema>;

/**
 * Escalating patient feedback to an incident (plan §1.5): the feedback module's contract.
 * Idempotent on `feedbackId`.
 */
export const escalateFeedbackRequestSchema = z.object({
  feedbackId: z.uuid(),
  ...reportFields,
  immediateAction: optionalText(L.immediateAction).optional(),
  patientId: z.uuid().nullable().optional(),
  visitId: z.uuid().nullable().optional(),
});
export type EscalateFeedbackRequest = z.infer<typeof escalateFeedbackRequestSchema>;

export const escalateFeedbackResponseSchema = z.object({
  incidentId: z.uuid(),
  incidentNumber: z.string(),
  /** False when this feedback was already escalated: the existing incident is returned. */
  created: z.boolean(),
});
export type EscalateFeedbackResponse = z.infer<typeof escalateFeedbackResponseSchema>;

const capturedSeveritySchema = z.object({
  code: z.string(),
  display: z.string(),
  /** 1 = least harm. */
  rank: z.number().int(),
});

export const incidentListItemSchema = z.object({
  id: z.uuid(),
  /** Mono. */
  incidentNumber: z.string(),
  category: z.object({ code: z.string(), display: z.string() }),
  severity: capturedSeveritySchema,
  occurredAt: z.iso.datetime(),
  reportedAt: z.iso.datetime(),
  location: z.string(),
  status: z.enum(INCIDENT_STATUSES),
  reviewer: actorSchema.nullable(),
  source: z.enum(INCIDENT_SOURCES),
  patientLinked: z.boolean(),
  openActions: z.number().int(),
  overdueActions: z.number().int(),
});
export type IncidentListItemView = z.infer<typeof incidentListItemSchema>;

export const incidentListSchema = z.object({ items: z.array(incidentListItemSchema) });
export type IncidentListView = z.infer<typeof incidentListSchema>;

export const INCIDENT_LIST_FILTERS = ['open', 'closed', 'all'] as const;

export const incidentActionSchema = z.object({
  id: z.uuid(),
  description: z.string(),
  owner: actorSchema,
  dueOn: isoDaySchema,
  status: z.enum(INCIDENT_ACTION_STATUSES),
  overdue: z.boolean(),
  outcomeNote: z.string().nullable(),
  completedAt: z.iso.datetime().nullable(),
  completedBy: actorSchema.nullable(),
});
export type IncidentActionView = z.infer<typeof incidentActionSchema>;

export const incidentEventSchema = z.object({
  id: z.uuid(),
  kind: z.enum(INCIDENT_EVENT_KINDS),
  at: z.iso.datetime(),
  by: actorSchema,
  fromStatus: z.enum(INCIDENT_STATUSES).nullable(),
  toStatus: z.enum(INCIDENT_STATUSES).nullable(),
  note: z.string().nullable(),
});
export type IncidentEventView = z.infer<typeof incidentEventSchema>;

/**
 * The linked patient, as this reader may see it: none linked, shown, or hidden because the
 * reader may not open patient records or the linked visit's sensitivity labels (ADR 0036).
 */
export const incidentPatientSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('none') }),
  z.object({ state: z.literal('hidden') }),
  z.object({
    state: z.literal('shown'),
    patient: z.object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() }),
    visit: z.object({ id: z.uuid(), visitNumber: z.string() }).nullable(),
  }),
]);
export type IncidentPatientView = z.infer<typeof incidentPatientSchema>;

export const incidentSchema = incidentListItemSchema.extend({
  description: z.string(),
  immediateAction: z.string().nullable(),
  equipmentText: z.string().nullable(),
  anonymous: z.boolean(),
  /** Null when anonymous and the reader doesn't manage incidents. */
  reporter: actorSchema.nullable(),
  reportedByMe: z.boolean(),
  patient: incidentPatientSchema,
  /** Null for a reader who only reported it (plan I3). */
  rootCause: z.string().nullable(),
  closingNote: z.string().nullable(),
  closedAt: z.iso.datetime().nullable(),
  statusReason: z.string().nullable(),
  actions: z.array(incidentActionSchema),
  events: z.array(incidentEventSchema),
  /** Whether this reader may review it. */
  mayManage: z.boolean(),
});
export type IncidentView = z.infer<typeof incidentSchema>;

export const incidentPeopleSchema = z.object({ items: z.array(actorSchema) });

export const startIncidentReviewRequestSchema = z.object({ reviewerId: z.uuid() });
export const incidentRootCauseRequestSchema = z.object({
  rootCause: z.string().trim().min(1).max(L.rootCause),
});
export const addIncidentActionRequestSchema = z.object({
  description: z.string().trim().min(1).max(L.actionDescription),
  ownerId: z.uuid(),
  dueOn: isoDaySchema,
});
export type AddIncidentActionRequest = z.infer<typeof addIncidentActionRequestSchema>;
export const completeIncidentActionRequestSchema = z.object({
  note: z.string().trim().min(1).max(L.note),
});
export const cancelIncidentActionRequestSchema = z.object({ reason: reasonSchema });
export const closeIncidentRequestSchema = z.object({
  closingNote: z.string().trim().min(1).max(L.note),
});
export const reopenIncidentRequestSchema = z.object({ reason: reasonSchema });
